/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAppTheme } from "../../utils/theme";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Button, Card, CardContent } from "@mui/material";
import { useUserStore } from "../../stores/user.store";
import { useCurrencySetting } from "../../hooks/useSetting";
import { useCreateAuditlog } from "../../hooks/useAuditlog";
import DataTable, { createDataRow } from "../../components/DataTable";
import ReportFilters, { ReportFiltersProps } from "./ReportFilters";
import { buttonExport } from "./Report.styles";
import { useQueryClient } from "@tanstack/react-query";
import { useSettings } from "../../hooks/useSetting";
import { formatCommaSeparatedNumber } from "../../utils/general";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useGetProvidersByProgramId } from "../../hooks/useBeneficiaries";
import { ValidQlikOptions } from "../../domain/report.interface";
import { useGetOrganizationList } from "../../hooks/useOrganizations";
import { useBeneficiaryTerm } from "../../hooks/useBeneficiaryTerm";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { QlikEmbed } = require("@qlik/embed-react");

const MultiTreatmentProviderReport: React.FC = () => {
	const theme = useAppTheme();
	const queryClient = useQueryClient();
	const { userInfo } = useUserStore();
	const currencySetting = useCurrencySetting();
	const createAuditlogMutation = useCreateAuditlog();
	const { data: settings } = useSettings(userInfo?.id);
	const { singular, plural } = useBeneficiaryTerm();

	const replaceTermInHeader = (header: string) =>
		header
			.replace(/\bBeneficiary\b/g, singular)
			.replace(/\bBeneficiaries\b/g, plural)
			.replace(/\bParticipant\b/g, singular)
			.replace(/\bParticipants\b/g, plural)
			.replace(/\bbenef\.?/gi, plural.toLowerCase());

	// Qlik-related state
	const APP_ID = process.env.REACT_APP_QLIK_APP_ID || "";
	const TABLE_OBJECT_ID = settings?.qlikProviderReportConfig?.tableObjectId;
	const EXPORT_OBJECT_ID = settings?.qlikProviderReportConfig?.tableObjectId;

	const [qlikApi, setQlikApi] = useState<any>(null);
	const [model, setModel] = useState<any>(null);
	const modelRef = useRef<any>(null);
	const [isLoading, setIsLoading] = useState(false);

	// Provider report data
	const [tableData, setTableData] = useState<Array<
		Array<{ qText: string; qNum?: number }>
	> | null>(null);
	const [headers, setHeaders] = useState<string[]>([]);
	const [validQlikOptions, setValidQlikOptions] = useState<ValidQlikOptions>(
		{},
	);

	useEffect(() => {
		if (model) {
			modelRef.current = model;
		}
	}, [model]);

	const [filters, setFilters] = useState<ReportFiltersProps>({
		programId: [], // Start empty, let ReportFilters initialize
		providerSiteId: [],
		providerId: "",
		providerNpi: "",
		beneficiary: "",
		beneficiaryCin: "",
	});

	//get the OUs
	const {
		data: providerSitesData,
		isFetching: ouFetching,
		isLoading: ouLoading,
	} = useGetOrganizationList(userInfo?.accountId || "");

	//get providers
	const {
		isLoading: isLoadingProviders,
		isFetching: isFetchingProviders,
		data: providers,
		refetch: refetchProviders,
	} = useGetProvidersByProgramId(
		filters.programId,
		filters.providerSiteId,
		false,
	);

	useEffect(() => {
		if (filters.programId.length > 0 && filters.providerSiteId.length > 0) {
			refetchProviders();
		}
	}, [filters.programId, filters.providerSiteId, refetchProviders]);

	const postAuditlog = async ({
		action,
		actionDetail,
		actionCode,
	}: {
		action: string;
		actionDetail: string;
		actionCode: string;
	}) => {
		await createAuditlogMutation.mutateAsync({
			appType: "WEB_BACK_OFFICE",
			module: "Reporting",
			option: "Provider Report",
			actionCode,
			action,
			detail: actionDetail,
			createdBy: userInfo?.id || "",
		});
	};

	useEffect(() => {
		if (!qlikApi || !TABLE_OBJECT_ID) return;
		(async () => {
			const { app } = await qlikApi.getDoc();
			const obj = await app.getObject(TABLE_OBJECT_ID);
			setModel(obj);
		})();
	}, [qlikApi, TABLE_OBJECT_ID]);

	// Qlik data fetching logic - direct object (no container-child)
	const fetchData = async () => {
		if (!model) return;
		setIsLoading(true);
		try {
			await model.app.clearAll();
			//TODO add acccountId env.accountId
			const programIds = filters.programId.flatMap((item) =>
				typeof item === "string" && item.includes(",")
					? item.split(",").map((id) => id.trim())
					: [item],
			);
			const qValues = Array.isArray(programIds)
				? programIds.map((v) => ({ qText: v }))
				: [{ qText: programIds }];

			await model.app
				.getField("program_id")
				.then((f: any) => f.selectValues(qValues, false, false))
				.catch((error: any) => {
					console.log("Field program_id not found, skipping selection:", error);
					return Promise.resolve();
				});

			model.on("changed", async () => {
				const currentModel = modelRef.current;
				console.log("model changed");
				await fetchHypercubeData(currentModel);
				await fetchValidQlikOptions();
			});

			await fetchHypercubeData(model);
			await fetchValidQlikOptions();
		} catch (error) {
			console.error("Error fetching Qlik data:", error);
			setIsLoading(false);
		}
	};

	// Apply filters to Qlik
	useEffect(() => {
		if (
			!qlikApi ||
			!filters.programId.length ||
			!filters.providerSiteId.length ||
			ouFetching ||
			ouLoading
		)
			return;
		setQlikFilters();
	}, [filters, qlikApi, ouFetching, ouLoading]);

	useEffect(() => {
		if (!qlikApi || !model || !filters.programId || !filters.providerSiteId)
			return;
		fetchData();
		fetchValidQlikOptions();
	}, [qlikApi, model]);

	const setQlikFilters = async () => {
		if (!qlikApi || !model) return;

		try {
			await model.app.clearAll();

			// Use variables for provider report filtering
			const promises = [];
			// Try direct field selections for other fields
			const select = (fieldName: string, values: string | string[]) => {
				const qValues = Array.isArray(values)
					? values.map((v) => ({ qText: v }))
					: [{ qText: values }];
				return model.app
					.getField(fieldName)
					.then((f: any) => {
						return f.selectValues(qValues, false, false);
					})
					.catch((error: any) => {
						console.log(
							`Field ${fieldName} not found, skipping selection:`,
							error,
						);
						return Promise.resolve();
					});
			};

			if (!ouLoading && !ouFetching && providerSitesData) {
				const ouNameList = providerSitesData
					.filter((ou) => filters.providerSiteId.includes(ou.id))
					.map((oun) => oun.id);
				promises.push(
					model.app
						.getVariableByName("vSelectedOU")
						.then((v: any) => v.setStringValue(ouNameList.join("|"))),
				);
			}

			if (filters.providerId) {
				if (!isLoadingProviders && !isFetchingProviders && providers) {
					const providerNameList = providers
						.filter((p) => p.key === filters.providerId)
						.map((p) => p.key);

					promises.push(
						model.app
							.getVariableByName("vSelectedProvider")
							.then((v: any) => v.setStringValue(providerNameList.join("|"))),
					);
				}
			}

			if (filters.providerNpi) {
				promises.push(select("NPI Provider", filters.providerNpi));
			}

			await Promise.all(promises);

			if (filters.programId && filters.programId.length > 0) {
				const programIds = filters.programId.flatMap((item) =>
					typeof item === "string" && item.includes(",")
						? item.split(",").map((id) => id.trim())
						: [item],
				);
				await model.app
					.getVariableByName("vSelectedProgram")
					.then((v: any) => v.setStringValue(programIds.join("|")));
			}

			// Fetch valid options after setting filters
			await fetchValidQlikOptions();
			//await fetchData();
		} catch (error) {
			console.error("Error setting Qlik filters:", error);
		}
	};

	const fetchValidQlikOptions = async () => {
		if (!qlikApi || !TABLE_OBJECT_ID) return;

		try {
			const layout: any = await model.getLayout();

			if (!layout.qHyperCube?.qDimensionInfo) return;

			const { qDimensionInfo } = layout.qHyperCube;
			const { qcy: totalRows, qcx: totalCols } = layout.qHyperCube.qSize;
			const pageSize = 1000;
			const numPages = Math.ceil(totalRows / pageSize);

			// Fetch all data pages
			const pages = await Promise.all(
				Array.from({ length: numPages }, (_, i) => {
					const qTop = i * pageSize;
					const qHeight = Math.min(pageSize, totalRows - qTop);
					return model
						.getHyperCubeData({
							qPath: "/qHyperCubeDef",
							qPages: [{ qLeft: 0, qTop, qWidth: totalCols, qHeight }],
						})
						.then((p: any[]) => p[0]);
				}),
			);

			// Flatten all rows
			const allRows = pages.flatMap((p) => p.qMatrix);

			// For each dimension, collect unique values with valid qState
			const validOptions: ValidQlikOptions = {};
			qDimensionInfo.forEach((dim: any, dimIdx: number) => {
				const fieldName = dim.qFallbackTitle;
				const fieldExpression = dim.qEffectiveDimensionName;

				// Collect unique values with valid qState for this dimension
				const valuesSet = new Set<string>();
				allRows.forEach((row) => {
					const cell = row[dimIdx];
					if (
						cell &&
						(cell.qState === "O" ||
							cell.qState === "S" ||
							cell.qState === "A") &&
						cell.qText &&
						cell.qText.trim() !== ""
					) {
						valuesSet.add(cell.qText);
					}
				});
				const uniqueValidValues = Array.from(valuesSet);

				if (fieldExpression === "program_id") {
					validOptions.programIds = uniqueValidValues;
				} else if (fieldExpression === "organizations.name") {
					validOptions.orgUnitIds = uniqueValidValues;
				} else if (
					fieldExpression === "=[users.firstName]&' '&[users.lastName]"
				) {
					validOptions.providerIds = uniqueValidValues;
				} else if (
					fieldName?.toLowerCase().includes("npi") ||
					fieldExpression?.toLowerCase().includes("npi")
				) {
					validOptions.providerNpis = uniqueValidValues;
				}
			});

			setValidQlikOptions(validOptions);
		} catch (error) {
			console.error("Error fetching valid Qlik options from table:", error);
		}
	};

	async function fetchHypercubeData(model: any) {
		try {
			const layout = await model.getLayout();
			const dimHeaders = layout.qHyperCube.qDimensionInfo.map(
				(d: any) => d.qFallbackTitle,
			);
			const measHeaders = layout.qHyperCube.qMeasureInfo.map(
				(m: any) => m.qFallbackTitle,
			);
			const allHeaders = [...dimHeaders, ...measHeaders];
			setHeaders(allHeaders.filter((h) => h !== "ProgramID"));

			const { qcy: totalRows, qcx: totalCols } = layout.qHyperCube.qSize;

			const pageSize = 1000;
			const numPages = Math.ceil(totalRows / pageSize);

			const pages = await Promise.all(
				Array.from({ length: numPages }, (_, i) => {
					const qTop = i * pageSize;
					const qHeight = Math.min(pageSize, totalRows - qTop);
					return model
						.getHyperCubeData({
							qPath: "/qHyperCubeDef",
							qPages: [{ qLeft: 0, qTop, qWidth: totalCols, qHeight }],
						})
						.then((p: any[]) => p[0]);
				}),
			);

			const rows = pages
				.flatMap((p) => p.qMatrix)
				.map((row) => row.slice(0, -1));
			setTableData(rows);
			setIsLoading(false);
		} catch (error) {
			console.error("Error fetching hypercube data:", error);
			setIsLoading(false);
		}
	}

	const pullExportData = async (
		qlikApi: any,
		exportObjectId: string,
	): Promise<{ headers: string[]; rows: string[][] }> => {
		const { app } = await qlikApi.getDoc();
		const model = await app.getObject(exportObjectId);

		const layout: any = await model.getLayout();
		const dims = layout.qHyperCube.qDimensionInfo.map(
			(d: any) => d.qFallbackTitle,
		);
		const measures = layout.qHyperCube.qMeasureInfo.map(
			(m: any) => m.qFallbackTitle,
		);
		const headers = [...dims, ...measures];
		const { qcy: totalRows, qcx: totalCols } = layout.qHyperCube.qSize;
		const pageSize = 200;
		const numPages = Math.ceil(totalRows / pageSize);
		const pages = await Promise.all(
			Array.from({ length: numPages }, (_, i) => {
				const qTop = i * pageSize;
				const qHeight = Math.min(pageSize, totalRows - qTop);
				return model
					.getHyperCubeData({
						qPath: "/qHyperCubeDef",
						qPages: [{ qLeft: 0, qTop, qWidth: totalCols, qHeight }],
					})
					.then(
						(p: any[]) =>
							p[0].qMatrix as Array<Array<{ qText: string; qNum?: number }>>,
					);
			}),
		);
		const matrix = pages.flat();

		const rows = matrix.map((row) =>
			row.map((cell: any) => String(cell.qText)),
		);

		const adjustedHeaders = headers
			.filter((h) => h !== "ProgramID")
			.map(replaceTermInHeader);
		const adjustedRows = rows.map((r) => r.slice(0, -1));

		return { headers: adjustedHeaders, rows: adjustedRows };
	};

	const handleExportExcel = async () => {
		if (!qlikApi || !EXPORT_OBJECT_ID) return;

		const { headers, rows } = await pullExportData(qlikApi, EXPORT_OBJECT_ID);

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
		ws["!cols"] = headers.map(() => ({ wch: 20 }));
		XLSX.utils.book_append_sheet(wb, ws, "Provider Report");
		XLSX.writeFile(wb, "provider_report.xlsx");
	};

	const handleExportPdf = async () => {
		if (!qlikApi || !EXPORT_OBJECT_ID) return;

		const { headers, rows } = await pullExportData(qlikApi, EXPORT_OBJECT_ID);

		const doc = new jsPDF({
			unit: "pt",
			format: "A4",
			orientation: "landscape",
		});

		autoTable(doc, {
			head: [headers],
			body: rows,
			styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
			headStyles: { fillColor: [41, 128, 185] },
			theme: "striped",
			margin: { top: 40, left: 40, right: 40, bottom: 40 },
		});

		doc.save("provider_report.pdf");
	};

	const onFiltersChange = (newFilters: ReportFiltersProps) => {
		// Always flatten providerSiteId to avoid nested arrays
		const flatProviderSiteId = Array.isArray(newFilters.providerSiteId)
			? newFilters.providerSiteId.flat()
			: [newFilters.providerSiteId];
		setFilters({
			...newFilters,
			providerSiteId: flatProviderSiteId,
		});
	};

	// Provider report rows
	const rows = useMemo(() => {
		if (!tableData || tableData.length === 0) return [];

		return tableData.map((rowArray, index) =>
			createDataRow(
				index,
				rowArray.reduce(
					(rec, cell, cidx) => {
						// Handle currency columns (incentive columns typically at the end)
						if (cidx >= headers.length - 2) {
							// Assuming last column are currency fields
							rec[`col${cidx}`] = (
								<span style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
									{`${currencySetting?.mask}${formatCommaSeparatedNumber(cell.qNum || 0)}`}
								</span>
							);
						} else if (cidx >= headers.length - 1) {
							// Assuming last column are currency fields
							rec[`col${cidx}`] = (
								<span style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
									{`${currencySetting?.mask}${formatCommaSeparatedNumber(cell.qNum || 0)}`}
								</span>
							);
						} else {
							rec[`col${cidx}`] = (
								<span style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
									{cell.qText}
								</span>
							);
						}
						return rec;
					},
					{} as Record<string, React.ReactNode>,
				),
			),
		);
	}, [tableData, headers, currencySetting]);

	// Dynamic head cells based on Qlik headers
	const headCells = useMemo(() => {
		if (!headers || headers.length === 0) return [];

		return headers.map((lbl, idx) => ({
			id: `col${idx}`,
			label: replaceTermInHeader(lbl),
			isSortable: false,
			disablePadding: false,
			align: "left" as const,
		}));
	}, [headers]);

	// Audit log for initial access
	useEffect(() => {
		postAuditlog({
			action: "When entering",
			actionDetail: "Access to multi-treatment provider report",
			actionCode: "WEB_REPORT_PROV_ACCESS",
		});
	}, []);

	useEffect(() => {
		return () => {
			queryClient.removeQueries({
				queryKey: ["sims-report-provider"],
			});
		};
	}, [queryClient]);

	return (
		<>
			<div className="w-full h-full overflow-y-auto scroll-smooth">
				<div className="container">
					<div className="flex flex-col items-center justify-start min-h-screen space-y-4">
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "flex-start",
								width: "100%",
							}}
						>
							<Typography variant="headlineSmall">
								Provider report
								<InfoOutlinedIcon />
							</Typography>
						</Box>

						<Card sx={{ minWidth: 275, width: "100%" }}>
							<CardContent className="space-y-5">
								<Box
									sx={{
										display: "flex",
										gap: "1rem",
									}}
								>
									<Button
										size="large"
										variant="outlined"
										sx={buttonExport(theme)}
										onClick={handleExportExcel}
										disabled={isLoading || !tableData || tableData.length === 0}
									>
										DOWNLOAD EXCEL
									</Button>
									<Button
										size="large"
										variant="outlined"
										sx={buttonExport(theme)}
										onClick={handleExportPdf}
										disabled={isLoading || !tableData || tableData.length === 0}
									>
										DOWNLOAD PDF
									</Button>
								</Box>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: {
											xs: "repeat(1, 1fr)",
											sm: "repeat(2, 1fr)",
											md: "repeat(4, 1fr)",
											lg: "repeat(4, 1fr)",
										},
										gap: "1rem",
										width: "100%",
									}}
								>
									{!model ? null : (
										<ReportFilters
											onFiltersChange={onFiltersChange}
											filters={filters}
											showBeneficiaryFilters={false}
											validOptions={validQlikOptions}
										/>
									)}
									<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
										{/* Hidden QlikEmbed component for API access */}
										<Box
											sx={{
												opacity: 0,
												visibility: "hidden",
												width: 0,
												height: 0,
											}}
										>
											<QlikEmbed
												ui="analytics/chart"
												appId={APP_ID}
												objectId={TABLE_OBJECT_ID}
												identity={`${userInfo?.id}-provider`}
												style={{ width: "100%", height: "100%" }}
												exposeRefApi={(ref: any) => {
													if (!qlikApi && ref) {
														setQlikApi(ref);
													}
												}}
											/>
										</Box>
									</Box>
								</Box>

								<DataTable
									data={rows}
									rowsPerPage={10}
									order="asc"
									orderBy="none"
									page={0}
									headCells={headCells}
									total={rows.length}
									isLoading={isLoading || !model}
									useFrontendPagination={true}
								/>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</>
	);
};

export default MultiTreatmentProviderReport;
